import { useCallback, useMemo } from 'react';

import { AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router';

import { useSyncState, useToggle } from '@hooks';
import { useTranslate } from '@i18n';
import { useActivity, useActivitySwings, useDeleteTag, useUpdateActivityTags, useTags, useUpsertTag } from '@queries';
import { Button, EditableTag, Screen, Spinner } from '@ui';

import { TagControls } from './TagControls';
import { TagModal } from './TagModal';
import { TagSaveModal } from './TagSaveModal';
import * as css from './TagSelection.css';
import { useTagEditModal } from './useTagEditModal';

type TagSelectionProps = {
    onCreateNewActivity?: () => Promise<number>;
    activityId: number;
    activeUserId?: string;
    onClose: () => void;
};

export function TagSelection({ onCreateNewActivity, activityId, onClose, activeUserId }: TagSelectionProps) {
    const { data: availableTags } = useTags(activeUserId);
    const { data: activity, isLoading: isActivityLoading } = useActivity(activityId);

    const { mutateAsync: deleteTag } = useDeleteTag(activeUserId);
    const { mutateAsync: upsertTag } = useUpsertTag(activeUserId);
    const { data: swings, isLoading } = useActivitySwings(activityId);
    const { editTagId, addTag, editTag, closeTagModal } = useTagEditModal();
    const { mutateAsync: updateActivityTags } = useUpdateActivityTags();

    const activityTagIds = useMemo(() => (activity?.tags || []).map(({ id }) => id), [activity?.tags]);

    const editTagName = useMemo(() => {
        return availableTags?.find((tag) => tag.id === editTagId)?.name;
    }, [availableTags, editTagId]);

    const [selectedTags, setSelectedTags] = useSyncState(activityTagIds, isActivityLoading);

    const [isSaving, toggleIsSaving] = useToggle(false);
    const [isEditable, setIsEditable] = useToggle(false);

    const navigate = useNavigate();

    const selectTag = useCallback(
        (tagId: number) => {
            setSelectedTags((prev) => {
                if (prev.some((id) => id === tagId)) {
                    return prev.filter((id) => id !== tagId);
                }
                return [...prev, tagId];
            });
        },
        [setSelectedTags],
    );

    const handleUpsert = useCallback(async (tagId: number, name: string) => {
        const isCreatingNewTag = tagId === -1;
        const tag = await upsertTag({ tagId, name });
        if (tag && tag.id && isCreatingNewTag) {
            selectTag(tag.id);
        }
    }, []);

    const handleTagClick = useCallback(
        (tagId: number) => {
            if (isEditable) {
                editTag(tagId);
            } else {
                selectTag(tagId);
            }
        },
        [editTag, isEditable, selectTag],
    );

    const handleSave = useCallback(async () => {
        const hasNoSwings = !swings?.length;
        // if the user has no swings and onCreate is defined that means
        // we are on kiosk and we should update the activity without asking
        if (hasNoSwings && onCreateNewActivity) {
            await updateActivityTags({
                activityId,
                tagIds: selectedTags,
            });
            onClose();
        }
        // otherwise default to normal flow
        toggleIsSaving();
    }, [swings, selectedTags, updateActivityTags, onClose, toggleIsSaving]);

    const t = useTranslate();

    return (
        <>
            <Screen.Root
                title={t('tag_selection.title')}
                header={{
                    static: {
                        end: (
                            <>
                                <Button variant="secondary" onClick={() => navigate(-1)}>
                                    {t('generic_ui.cancel')}
                                </Button>
                                <Button variant="primary" isLoading={isLoading} onClick={handleSave}>
                                    {t('generic_ui.save')}
                                </Button>
                            </>
                        ),
                    },
                }}
            >
                <Screen.Column span="20/20">
                    <div className={css.root}>
                        <div className={css.content}>
                            <div className={css.tags}>
                                {availableTags ? (
                                    availableTags.map(({ id, name }) => (
                                        <EditableTag
                                            key={id}
                                            onClick={() => handleTagClick(id)}
                                            isSelected={selectedTags.some((tagId) => tagId === id)}
                                            isEditable={isEditable}
                                        >
                                            {name}
                                        </EditableTag>
                                    ))
                                ) : (
                                    <Spinner size="l" />
                                )}
                            </div>

                            <TagControls onAddTag={addTag} onToggleEditable={setIsEditable} isEditable={isEditable} />
                        </div>
                    </div>
                </Screen.Column>
            </Screen.Root>

            <AnimatePresence>
                {editTagId !== null && (
                    <TagModal
                        tagId={editTagId}
                        tagName={editTagName}
                        onClose={closeTagModal}
                        onSave={handleUpsert}
                        onDelete={deleteTag}
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {isSaving && (
                    <TagSaveModal
                        onCreateNewActivity={onCreateNewActivity}
                        activityId={activityId}
                        onDismiss={toggleIsSaving}
                        selectedTags={selectedTags}
                        onClose={onClose}
                    />
                )}
            </AnimatePresence>
        </>
    );
}
